import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, unref as _unref, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, withCtx as _withCtx, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createElementBlock as _createElementBlock, createVNode as _createVNode } from "vue"

const _hoisted_1 = { class: "project-expenses" }
const _hoisted_2 = {
  key: 1,
  class: "already-invoiced-warning"
}
const _hoisted_3 = { class: "table__footer" }
const _hoisted_4 = {
  key: 1,
  class: "empty"
}
const _hoisted_5 = { class: "bottom-buttons" }

import { computed, ref, reactive, watch } from "vue";
import { useQuery } from "@vue/apollo-composable";
import { useI18n } from "vue-i18n";
import { type PaginatedResult } from "@/helpers/typesDefinition";
import { type Expense } from "@/helpers/project/typesDefinition";
import { formatPrice } from "@/helpers/utils";
import TableBase, { type TableBaseColumn } from "@/components/TableBase.vue";
import BasicButton from "@/components/BasicButton.vue";
import checkHasAccess from "@/helpers/permissions/checkHasAccess";
import { GET_PROJECT_FOR_ACCESS_RIGHTS } from "@/graphql/project/queries";
import { useToast } from "vue-toastification";
import { GET_PROJECT_EXPENSES } from "@/graphql/expenses/queries";
import ExpenseDetails from "@/components/expense/ExpenseDetails.vue";
import AddExpense from "@/components/expense/AddExpense.vue";
import AddInvoiceForExpense from "@/components/CRUXEntity/AddInvoiceForExpense.vue";
import config from "@/config";
type GetProjectExpensesResult = {
  projectExpenses: PaginatedResult<Expense>;
};


export default /*@__PURE__*/_defineComponent({
  __name: 'ProjectExpenses',
  props: {
    id: {},
    expenseId: {}
  },
  setup(__props: any) {

const backEndUri = config.backendUri;

const props = __props;

const { t } = useI18n({
  inheritLocale: true,
  useScope: "global",
});
const toast = useToast();

const getProjectExpensesVariables = computed(() => {
  const projectId = props.id;
  if (projectId) {
    return {
      offset: 0,
      projectId,
    };
  }
  return {};
});

const { result: expensesResult, fetchMore } =
  useQuery<GetProjectExpensesResult>(
    GET_PROJECT_EXPENSES,
    getProjectExpensesVariables,
  );

const refetchQueries = computed(() => [
  {
    query: GET_PROJECT_EXPENSES,
    variables: getProjectExpensesVariables.value,
  },
]);

const expenses = computed(() => {
  return expensesResult.value?.projectExpenses.items;
});

const hasMore = computed(() => {
  return expensesResult.value?.projectExpenses?.hasMore;
});

const { result: projectResult } = useQuery(GET_PROJECT_FOR_ACCESS_RIGHTS, {
  id: props.id,
});

const loadMore = () => {
  if (hasMore.value) {
    fetchMore({
      variables: {
        projectId: props.id,
        offset: expenses.value?.length,
      },
    });
  }
};

const hasAdminAccess = ref(false);
const hasClientAccess = ref(false);
const readOnly = ref(true);

watch([projectResult], async ([val]) => {
  const project = val?.project;
  if (!project) return;
  readOnly.value = project.active;
  hasAdminAccess.value = await checkHasAccess(
    project.client.id,
    props.id,
    "admin",
    toast,
    t("errors.getUserDetails"),
  );
  hasClientAccess.value = await checkHasAccess(
    project.client.id,
    props.id,
    "client",
    toast,
    t("errors.getUserDetails"),
  );
});

const columns: TableBaseColumn[] = [
  {
    name: "name",
    title: t("labels.expenses.name"),
  },
  {
    name: "date",
    title: t("labels.expenses.date"),
  },
  {
    name: "costNet",
    title: t("labels.expenses.costNet"),
  },
  {
    name: "costGross",
    title: t("labels.expenses.costGross"),
  },
  {
    name: "quantity",
    title: t("labels.expenses.quantity"),
  },
];

const references = reactive<Record<string, HTMLElement | null>>({});

const getRef = (expenseId: string, element: HTMLElement | null) => {
  references[expenseId] = element;
};

const formattedExpenses = computed(() => {
  const raw = expensesResult.value?.projectExpenses.items || [];

  return raw.map((exp) => ({
    ...exp,
    costNet: formatPrice(exp.costNet),
    costGross: formatPrice(exp.costGross),
    quantity: String(exp.quantity),
  }));
});

const modalExpenseId = ref(props.expenseId ?? "");

const isModalDetailsVisible = ref(false);
function showModalDetails(): void {
  isModalDetailsVisible.value = true;
}

function closeExpenseDetailsModal(): void {
  isModalDetailsVisible.value = false;
}

const isAddExpenseModalVisible = ref(false);
function showAddExpenseModal(): void {
  isAddExpenseModalVisible.value = true;
}

function closeAddExpenseModal(): void {
  isAddExpenseModalVisible.value = false;
}

const selectExpense = (expenseId: string) => {
  modalExpenseId.value = expenseId;
};

const fullSelectedExpense = computed<Expense | null>(() => {
  if (!expenses.value) return null;
  return expenses.value.find((e) => e.id === modalExpenseId.value) ?? null;
});

const isAddInvoiceModalVisible = ref(false);
function showAddInvoiceModal(expenseId: string, event: Event): void {
  event.stopPropagation();
  selectExpense(expenseId);
  isAddInvoiceModalVisible.value = true;
}

function showInvoice(invoiceId: string, event: Event): void {
  event.stopPropagation();
  const url = `${backEndUri}/documents/invoice/${invoiceId}`;

  const link = document.createElement("a");
  link.href = url;
  link.target = "_blank";
  link.rel = "noopener";
  link.click();
}

function closeAddInvoiceModal(): void {
  isAddInvoiceModalVisible.value = false;
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createVNode(TableBase, {
      columns: columns,
      items: formattedExpenses.value,
      onSelectItem: selectExpense,
      onClick: showModalDetails,
      "is-clickable": true
    }, {
      actions: _withCtx(({ item: expense }) => [
        _createElementVNode("div", {
          class: "cell cell--actions",
          ref: (el) => getRef(expense.id, el as HTMLElement | null)
        }, null, 512),
        (expense.invoiceId)
          ? (_openBlock(), _createBlock(BasicButton, {
              key: 0,
              onClick: (event) => showInvoice(expense.invoiceId, event)
            }, {
              default: _withCtx(() => [
                _createTextVNode(_toDisplayString(_unref(t)("buttons.expenses.seeInvoice")), 1)
              ]),
              _: 1
            }, 8, ["onClick"]))
          : (expense.alreadyInvoiced)
            ? (_openBlock(), _createElementBlock("span", _hoisted_2, _toDisplayString(_unref(t)("labels.expenses.alreadyInvoiced")), 1))
            : (_openBlock(), _createBlock(BasicButton, {
                key: 2,
                onClick: (event) => showAddInvoiceModal(expense.id, event)
              }, {
                default: _withCtx(() => [
                  _createTextVNode(_toDisplayString(_unref(t)("buttons.expenses.invoiceNow")), 1)
                ]),
                _: 1
              }, 8, ["onClick"]))
      ]),
      _: 1
    }, 8, ["items"]),
    _createElementVNode("div", _hoisted_3, [
      (hasMore.value)
        ? (_openBlock(), _createBlock(BasicButton, {
            key: 0,
            onClick: loadMore
          }, {
            default: _withCtx(() => [
              _createTextVNode(_toDisplayString(_ctx.$t("buttons.expenses.loadMore")), 1)
            ]),
            _: 1
          }))
        : (_openBlock(), _createElementBlock("p", _hoisted_4, _toDisplayString(_ctx.$t("expenses.allLoaded")), 1))
    ]),
    (isModalDetailsVisible.value && fullSelectedExpense.value)
      ? (_openBlock(), _createBlock(ExpenseDetails, {
          key: 0,
          id: modalExpenseId.value,
          onClose: _cache[0] || (_cache[0] = ($event: any) => (closeExpenseDetailsModal())),
          project: _unref(projectResult).project,
          expense: fullSelectedExpense.value,
          "has-client-access": hasClientAccess.value,
          hasAdminAccess: hasAdminAccess.value,
          refetchQueries: refetchQueries.value
        }, null, 8, ["id", "project", "expense", "has-client-access", "hasAdminAccess", "refetchQueries"]))
      : _createCommentVNode("", true),
    (
        _unref(projectResult) &&
        _unref(projectResult).project.active &&
        hasAdminAccess.value &&
        isAddExpenseModalVisible.value
      )
      ? (_openBlock(), _createBlock(AddExpense, {
          key: 1,
          onClose: _cache[1] || (_cache[1] = ($event: any) => (closeAddExpenseModal())),
          project: _unref(projectResult).project,
          refetchQueries: refetchQueries.value
        }, null, 8, ["project", "refetchQueries"]))
      : _createCommentVNode("", true),
    (isAddInvoiceModalVisible.value && fullSelectedExpense.value)
      ? (_openBlock(), _createBlock(AddInvoiceForExpense, {
          key: 2,
          onClose: _cache[2] || (_cache[2] = ($event: any) => (closeAddInvoiceModal())),
          projectId: _unref(projectResult).project.id,
          expenseId: fullSelectedExpense.value.id,
          refetchQueries: refetchQueries.value
        }, null, 8, ["projectId", "expenseId", "refetchQueries"]))
      : _createCommentVNode("", true),
    _createElementVNode("div", _hoisted_5, [
      (hasAdminAccess.value)
        ? (_openBlock(), _createBlock(BasicButton, {
            key: 0,
            onClick: _cache[3] || (_cache[3] = ($event: any) => (showAddExpenseModal())),
            class: "add-button"
          }, {
            default: _withCtx(() => [
              _createTextVNode(_toDisplayString(_unref(t)("buttons.expenses.add")), 1)
            ]),
            _: 1
          }))
        : _createCommentVNode("", true)
    ])
  ]))
}
}

})